import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { listarOperacoes, obterEstatisticas, deletarOperacao, copiarOperacao } from '../../services/montagemPalete';
import { listarClientes } from '../../services/clientes';
import { listarFiliais } from '../../services/montagemPalete';
import type { MontagemPaleteOperacao, StatusOperacao, MontagemEstatisticas } from '../../types/montagemPalete';
import type { Filial } from '../../types/montagemPalete';
import { getHojeISO, toInicioDiaISO, toFimDiaISO } from '../../utils/dateHelpers';
import './MontagemPaletes.css';

const STATUS_LABELS: Record<StatusOperacao, string> = {
  pendente: 'Pendente',
  em_conferencia: 'Em Conferência',
  em_carregamento: 'Em Carregamento',
  em_transito: 'Em Trânsito',
  concluida: 'Concluída',
  cancelada: 'Cancelada'
};

const MontagemPaleteList: React.FC = () => {
  const navigate = useNavigate();
  const [operacoes, setOperacoes] = useState<MontagemPaleteOperacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroFilial, setFiltroFilial] = useState('');
  const [filtroDataInicio, setFiltroDataInicio] = useState<string>(getHojeISO());
  const [filtroDataFim, setFiltroDataFim] = useState<string>(getHojeISO());
  const [filtroNumeroPedido, setFiltroNumeroPedido] = useState('');
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState('');
  const [estatisticas, setEstatisticas] = useState<MontagemEstatisticas | null>(null);
  const [clientesOptions, setClientesOptions] = useState<{ id: number; razao_social: string }[]>([]);
  const [filiaisOptions, setFiliaisOptions] = useState<Filial[]>([]);

  const carregarOperacoes = useCallback(async () => {
    try {
      setLoading(true);
      const params: Record<string, string | number> = { page, limit: 10 };
      if (filtroStatus) params.status = filtroStatus;
      if (filtroCliente) params.cliente_id = filtroCliente;
      if (filtroFilial) params.filial_id = filtroFilial;
      if (busca) params.busca = busca;
      const numeroPedidoTrim = filtroNumeroPedido.trim();
      if (numeroPedidoTrim) params.numero_pedido = numeroPedidoTrim;
      // Busca por identificador (numero pedido ou busca) desconsidera data
      // e pesquisa todo historico.
      const buscaAtiva = Boolean((busca && busca.trim()) || numeroPedidoTrim);
      const dataInicioParam = buscaAtiva ? undefined : toInicioDiaISO(filtroDataInicio);
      const dataFimParam = buscaAtiva ? undefined : toFimDiaISO(filtroDataFim);
      if (dataInicioParam) params.dataInicio = dataInicioParam;
      if (dataFimParam) params.dataFim = dataFimParam;

      const result = await listarOperacoes(params);
      if (result.success) {
        setOperacoes(result.operacoes);
        setTotalPages(result.pagination.totalPages);
      }
    } catch (error: any) {
      setErro(error?.message || 'Erro ao carregar operações');
    } finally {
      setLoading(false);
    }
  }, [page, filtroStatus, filtroCliente, filtroFilial, busca, filtroDataInicio, filtroDataFim, filtroNumeroPedido]);

  useEffect(() => {
    carregarOperacoes();
  }, [carregarOperacoes]);

  useEffect(() => {
    const buscaAtiva = Boolean((busca && busca.trim()) || filtroNumeroPedido.trim());
    obterEstatisticas({
      dataInicio: buscaAtiva ? undefined : toInicioDiaISO(filtroDataInicio),
      dataFim: buscaAtiva ? undefined : toFimDiaISO(filtroDataFim)
    }).then(res => {
      if (res.success) setEstatisticas(res.estatisticas);
    }).catch((err: any) => setErro(err?.message || 'Erro ao carregar estatísticas'));
  }, [filtroDataInicio, filtroDataFim, busca, filtroNumeroPedido]);

  useEffect(() => {
    listarClientes({ limit: 200, ativo: 'true' }).then(res => {
      if (res.success && res.clientes) {
        setClientesOptions(res.clientes.map((c: any) => ({ id: c.id, razao_social: c.razao_social })));
      }
    }).catch((err: any) => setErro(err?.message || 'Erro ao carregar clientes'));

    listarFiliais({ ativo: 'true' }).then(res => {
      if (res.success) setFiliaisOptions(res.filiais);
    }).catch((err: any) => setErro(err?.message || 'Erro ao carregar filiais'));
  }, []);

  const handleCopiar = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Deseja copiar esta operação?')) return;
    try {
      setErro('');
      const result = await copiarOperacao(id);
      if (result.success) {
        navigate(`/montar-carga/${result.operacao.id}`);
      }
    } catch (err: any) {
      const msg = err?.message || 'Erro ao copiar operação';
      setErro(msg);
    }
  };

  const handleExcluir = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Tem certeza que deseja excluir esta operação?')) return;
    try {
      setErro('');
      await deletarOperacao(id);
      carregarOperacoes();
    } catch (err: any) {
      const msg = err?.message || 'Erro ao excluir operação';
      setErro(msg);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>
          <i className="fas fa-pallet" style={{ marginRight: 8 }}></i>
          Montar Carga
        </h1>
        <div style={{ display: 'flex', gap: 12 }}>
          <button className="btn btn-outline" onClick={() => navigate('/montar-carga/agendamentos')}>
            <i className="fas fa-calendar-alt"></i>
            Agendamentos
          </button>
<button className="btn btn-primary" onClick={() => navigate('/montar-carga/novo')}>
            <i className="fas fa-plus"></i>
            Nova Operação
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      {estatisticas && (
        <div className="montagem-stats-grid">
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-clipboard-list" style={{ color: '#3498db' }}></i>
              <span>Total</span>
            </div>
            <p className="stat-value">{estatisticas.total}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-hourglass-start" style={{ color: '#0c5460' }}></i>
              <span>Pendentes</span>
            </div>
            <p className="stat-value" style={{ color: '#0c5460' }}>{estatisticas.pendentes}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-clipboard-check" style={{ color: '#856404' }}></i>
              <span>Em Conferência</span>
            </div>
            <p className="stat-value" style={{ color: '#856404' }}>{estatisticas.em_conferencia}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-truck-loading" style={{ color: '#e67e22' }}></i>
              <span>Em Carregamento</span>
            </div>
            <p className="stat-value" style={{ color: '#e67e22' }}>{estatisticas.em_carregamento}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-truck" style={{ color: '#2980b9' }}></i>
              <span>Em Trânsito</span>
            </div>
            <p className="stat-value" style={{ color: '#2980b9' }}>{estatisticas.em_transito}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-check-circle" style={{ color: '#27ae60' }}></i>
              <span>Concluídas</span>
            </div>
            <p className="stat-value" style={{ color: '#27ae60' }}>{estatisticas.concluidas}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-boxes-stacked" style={{ color: '#8e44ad' }}></i>
              <span>Total Paletes</span>
            </div>
            <p className="stat-value" style={{ color: '#8e44ad' }}>{estatisticas.total_paletes}</p>
          </div>
          <div className="montagem-stat-card">
            <div className="stat-header">
              <i className="fas fa-calendar-alt" style={{ color: '#a04000' }}></i>
              <span>Agendamentos</span>
            </div>
            <p className="stat-value" style={{ color: '#a04000' }}>{estatisticas.total_agendamentos}</p>
          </div>
        </div>
      )}

      {/* Mensagem de Erro */}
      {erro && (
        <div className="montagem-error" style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
          <i className="fas fa-exclamation-circle"></i>
          <span>{erro}</span>
          <button onClick={() => setErro('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '1rem' }}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      )}

      <div className="page-content">
        {/* Filtros */}
        <div className="montagem-filters">
          <div className="filter-input">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar por nº operação ou cliente..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
            <option value="">Todos os status</option>
            <option value="pendente">Pendente</option>
            <option value="em_conferencia">Em Conferência</option>
            <option value="em_carregamento">Em Carregamento</option>
            <option value="em_transito">Em Trânsito</option>
            <option value="concluida">Concluída</option>
            <option value="cancelada">Cancelada</option>
          </select>
          <select value={filtroCliente} onChange={(e) => setFiltroCliente(e.target.value)}>
            <option value="">Todos os clientes</option>
            {clientesOptions.map(c => (
              <option key={c.id} value={c.id}>{c.razao_social}</option>
            ))}
          </select>
          <select value={filtroFilial} onChange={(e) => setFiltroFilial(e.target.value)}>
            <option value="">Todas as filiais</option>
            {filiaisOptions.map(f => (
              <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social}</option>
            ))}
          </select>
          <input
            type="date"
            value={filtroDataInicio}
            onChange={(e) => { setFiltroDataInicio(e.target.value); setPage(1); }}
            title="Data inicial (emissão)"
          />
          <input
            type="date"
            value={filtroDataFim}
            onChange={(e) => { setFiltroDataFim(e.target.value); setPage(1); }}
            title="Data final (emissão)"
          />
          <input
            type="text"
            placeholder="Nº do pedido..."
            value={filtroNumeroPedido}
            onChange={(e) => { setFiltroNumeroPedido(e.target.value); setPage(1); }}
            title="Filtrar por número do pedido"
            style={{ minWidth: 160 }}
          />
        </div>

        {/* Tabela */}
        <div className="card">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
              <div className="loading-spinner-large"></div>
              <p>Carregando operações...</p>
            </div>
          ) : operacoes.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#7f8c8d' }}>
              <i className="fas fa-pallet" style={{ fontSize: '3rem', color: '#ddd', marginBottom: 16, display: 'block' }}></i>
              <p>Nenhuma operação encontrada</p>
            </div>
          ) : (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nº Operação</th>
                    <th>Cliente</th>
                    <th style={{ width: 80 }}>Filial</th>
                    <th>Local Pré-Carreg.</th>
                    <th>Paletes</th>
                    <th>Status</th>
                    <th>Data Emissão</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {operacoes.map((op) => (
                    <tr
                      key={op.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/montar-carga/${op.id}`)}
                    >
                      <td style={{ fontWeight: 600 }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <i className="fas fa-pallet" style={{ color: '#3498db' }}></i>
                          #{op.numero_operacao}
                        </span>
                      </td>
                      <td>{op.cliente_razao_social || '-'}</td>
                      <td>{op.filial_nome || '-'}</td>
                      <td>
                        {op.local_pre_carregamento === 'cliente' ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <i className="fas fa-building" style={{ color: '#7f8c8d' }}></i>
                            No Cliente
                          </span>
                        ) : (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <i className="fas fa-warehouse" style={{ color: '#7f8c8d' }}></i>
                            Base Transportadora
                          </span>
                        )}
                      </td>
                      <td>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <i className="fas fa-boxes-stacked" style={{ color: '#8e44ad' }}></i>
                          {op.total_paletes || 0}
                        </span>
                      </td>
                      <td>
                        <span className={`status-badge status-montagem-${op.status}`}>
                          {STATUS_LABELS[op.status]}
                        </span>
                      </td>
                      <td>{new Date(op.data_emissao).toLocaleDateString('pt-BR')} {new Date(op.data_emissao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="action-buttons" onClick={(e) => e.stopPropagation()} style={{ justifyContent: 'flex-end' }}>
                          <button className="btn-view" onClick={(e) => { e.stopPropagation(); navigate(`/montar-carga/${op.id}`); }} title="Visualizar">
                            <i className="fas fa-eye"></i>
                          </button>
                          <button className="btn-edit" onClick={(e) => { e.stopPropagation(); navigate(`/montar-carga/${op.id}/editar`); }} title="Editar">
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn-edit" onClick={(e) => handleCopiar(op.id, e)} title="Copiar Operação">
                            <i className="fas fa-copy"></i>
                          </button>
                          <button className="btn-delete" onClick={(e) => handleExcluir(op.id, e)} title="Excluir">
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="pagination">
              <button className="pagination-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <i className="fas fa-chevron-left"></i> Anterior
              </button>
              <span className="pagination-info">Página {page} de {totalPages}</span>
              <button className="pagination-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                Próxima <i className="fas fa-chevron-right"></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MontagemPaleteList;
